import React, { useEffect, useState } from "react";
import Modal from "@mui/joy/Modal";
import ModalDialog from "@mui/joy/ModalDialog";
import DialogTitle from "@mui/joy/DialogTitle";
import Stack from "@mui/joy/Stack";
import Input from "@mui/joy/Input";
import FormControl from "@mui/joy/FormControl";
import FormHelperText from "@mui/joy/FormHelperText";
import { Button, IconButton, CircularProgress } from "@mui/joy";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  validateEntityName,
  reindexErrors,
} from "../../../utils/validateEntityName";

const validateCompanyName = (name: string) =>
  validateEntityName(name, "Company name");

interface AddCompanyModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: Record<string, any>) => void;
}

const AddCompanyModal: React.FC<AddCompanyModalProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const [companies, setCompanies] = useState<string[]>([""]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  useEffect(() => {
    if (!open) {
      setCompanies([""]);
      setLoading(false);
      setErrors({});
      setSubmitAttempted(false);
    }
  }, [open]);

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    event.preventDefault();
    const pastedText = event.clipboardData.getData("text");
    const splitCompanies = pastedText
      .split(/\r?\n/)
      .map((c) => c.trim())
      .filter(Boolean);

    setCompanies((prev) => {
      const newCompanies = [...prev];
      newCompanies.splice(index, 1, ...splitCompanies);
      return newCompanies;
    });

    if (submitAttempted) {
      setErrors((prev) => {
        const next: Record<number, string> = {};
        const offset = splitCompanies.length - 1;
        Object.entries(prev).forEach(([k, v]) => {
          const key = Number(k);
          if (key < index) next[key] = v;
          else if (key > index) next[key + offset] = v;
        });
        splitCompanies.forEach((name, i) => {
          const error = validateCompanyName(name);
          if (error) next[index + i] = error;
        });
        return next;
      });
    }
  };

  const handleInputChange = (value: string, index: number) => {
    if (submitAttempted) {
      const error = validateCompanyName(value);
      setErrors((prev) => {
        const next = { ...prev };
        if (error) {
          next[index] = error;
        } else {
          delete next[index];
        }
        return next;
      });
    }

    setCompanies((prev) => {
      const newCompanies = [...prev];
      newCompanies[index] = value;

      if (index === newCompanies.length - 1 && value.trim() !== "") {
        return [...newCompanies, ""];
      }

      return newCompanies;
    });
  };

  const handleDelete = (index: number) => {
    setCompanies((prev) => prev.filter((_, i) => i !== index));
    setErrors((prev) => reindexErrors(prev, index));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);

    const filteredCompanies = companies
      .map((c, i) => ({ name: c.trim(), index: i }))
      .filter((c) => c.name !== "");

    if (filteredCompanies.length === 0) {
      setErrors({ 0: "Enter at least one company name" });
      return;
    }

    const validationErrors: Record<number, string> = {};
    for (const { name, index } of filteredCompanies) {
      const error = validateCompanyName(name);
      if (error) {
        validationErrors[index] = error;
      }
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    for (const { name } of filteredCompanies) {
      const data = {
        method: "manual",
        data: {
          index_name: "company",
          document: {
            name,
          },
        },
      };

      try {
        await onAdd(data);
      } catch (error) {
        console.error(`Failed to upload company: ${name}`, error);
      }
    }
    setLoading(false);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        className="modal"
        sx={{ maxHeight: "80vh", overflowY: "auto" }}
      >
        <DialogTitle>Add Companies</DialogTitle>
        <Stack spacing={2}>
          {companies.map((company, idx) => (
            <FormControl key={idx} error={!!errors[idx]}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Input
                  autoFocus={false}
                  placeholder="Company name"
                  value={company}
                  color={errors[idx] ? "danger" : "neutral"}
                  onChange={(e) => handleInputChange(e.target.value, idx)}
                  onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                    handlePaste(e, idx)
                  }
                  sx={{ flexGrow: 1 }}
                />
                {company.trim() !== "" && (
                  <IconButton
                    size="sm"
                    color="danger"
                    onClick={() => handleDelete(idx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
              {errors[idx] && <FormHelperText>{errors[idx]}</FormHelperText>}
            </FormControl>
          ))}
          {loading ? (
            <CircularProgress />
          ) : (
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="solid" color="neutral" onClick={onClose}>
                Close
              </Button>
              <Button variant="solid" color="primary" onClick={handleSubmit}>
                Submit
              </Button>
            </Stack>
          )}
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default AddCompanyModal;
